const PRODUCT_KEY_ERROR_FALLBACK =
  "We couldn't validate this product key. Please try again or contact support.";

type AuthErrorMessageOptions = {
  hasProductKey: boolean;
  isRegisterForm: boolean;
};

export const sanitizeAuthErrorMessage = (
  message: string,
  options?: AuthErrorMessageOptions
): string => {
  const normalizedMessage = message.trim();
  if (!normalizedMessage) return 'An error occurred';

  const isInternalRuntimeMessage =
    /^Cannot convert undefined or null to object$/i.test(normalizedMessage) ||
    /^Cannot read (?:properties|property) of undefined/i.test(
      normalizedMessage
    );

  if (
    isInternalRuntimeMessage &&
    options?.isRegisterForm &&
    options?.hasProductKey
  ) {
    return PRODUCT_KEY_ERROR_FALLBACK;
  }

  return normalizedMessage;
};

export const getErrorMessage = (
  error: unknown,
  options?: AuthErrorMessageOptions
): string => {
  if (error instanceof Error) {
    if (
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response &&
      error.response.data &&
      typeof error.response.data === 'object' &&
      'message' in error.response.data &&
      typeof error.response.data.message === 'string'
    ) {
      return sanitizeAuthErrorMessage(error.response.data.message, options);
    }

    return sanitizeAuthErrorMessage(error.message, options);
  }

  return 'An error occurred';
};

export const HOST_ACCESS_DENIED_ERROR = 'HostAccessDenied';

export const getHostAccessDeniedMessage = (appName = 'this product'): string =>
  `This account isn’t connected to ${appName}. Please use the sign-in site provided by your organization, or contact your administrator if you believe this is an error.`;

export const mapSignInErrorToUserMessage = (
  errorCode: string,
  options?: { appName?: string }
): string => {
  switch (errorCode) {
    case HOST_ACCESS_DENIED_ERROR:
      return getHostAccessDeniedMessage(options?.appName);
    case 'Incorrect email or password':
      return 'Incorrect email or password.';
    case 'Credentials are missing':
      return 'Sign-in details are missing. Please enter email and password.';
    case 'Server error during authentication.':
      return 'A server error occurred during sign-in. Please try again later.';
    case 'Account not configured for password login.':
      return 'This account may not be set up for password sign-in. Try another sign-in method or contact support.';
    case 'User account is inactive.':
      return 'Your account is currently inactive. Please contact support.';
    case 'User email not verified.':
      return 'Your email address has not been verified. Please check your inbox or contact support.';
    case 'Signin':
    case 'OAuthSignin':
    case 'OAuthCallback':
    case 'EmailCreateAccount':
    case 'Callback':
    case 'OAuthAccountNotLinked':
      return 'An unexpected issue occurred during sign-in. Please try again or use another method.';
    case 'OAuthCreateAccount':
      return 'We could not find a MindLabs student account linked to your Clever login. Please ask your teacher to sync your account in MindLabs before signing in.';
    case 'studentNotFound':
      return 'Educators must set up your MindLabs student account and sync it from Clever before you can sign in. Please ask your teacher for help.';
    case 'CredentialsSignin':
      return 'Incorrect email or password.';
    case 'SessionRequired':
      return 'Your session has expired or is invalid. Please sign in again.';
    default:
      return 'An unexpected error prevented sign-in. Please try again later or contact support if the issue persists.';
  }
};
